import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { listenFormValue$ } from '@app/shared/utils/helpers';
import { DropdownModule } from 'primeng/dropdown';
import { InputMaskModule } from 'primeng/inputmask';
import { Observable, tap } from 'rxjs';
import { ControlFieldComponent } from '../control-field/control-field.component';
import { DropdownOption } from '../dropdown/dropdown.model';
import { PrimeNgControlComponent } from '../prime-ng-control/prime-ng-control.component';
import {
  PhoneNumberControls,
  PhoneNumberValue,
} from './phone-number-field.model';

@Component({
  selector: 'ui-phone-number-field',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DropdownModule,
    ControlFieldComponent,
    InputMaskModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberFieldComponent),
      multi: true,
    },
  ],
  templateUrl: './phone-number-field.component.html',
  styleUrl: './phone-number-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneNumberFieldComponent
  extends PrimeNgControlComponent<PhoneNumberValue>
  implements OnInit
{
  private readonly fb = inject(NonNullableFormBuilder);

  public readonly options = input.required<DropdownOption<string>[]>();

  protected readonly form = this.fb.group<PhoneNumberControls>({
    phonePrefix: this.fb.control('', { validators: Validators.required }),
    phone: this.fb.control('', { validators: Validators.required }),
  });

  public ngOnInit(): void {
    this.formChanges$().subscribe();
  }

  private formChanges$(): Observable<void> {
    return listenFormValue$(this.form).pipe(
      tap((value) => {
        this.updateValue({
          phonePrefix: value.phonePrefix,
          phone: value.phone.replaceAll('-', ''),
        });
      }),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public override writeValue(value: PhoneNumberValue | null): void {
    if (!value) {
      this.form.reset(
        {
          phonePrefix: '',
          phone: '',
        },
        {
          emitEvent: false,
        },
      );

      return;
    }

    this.form.patchValue(value, {
      emitEvent: false,
    });
  }

  public override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);

    if (isDisabled) {
      this.form.disable({
        emitEvent: false,
      });

      return;
    }

    this.form.enable({
      emitEvent: false,
    });
  }

  protected override markAsTouched(): void {
    this.form.markAllAsTouched();

    super.markAsTouched();
  }
}
